import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, X, TrendingUp, Target, BookOpen, Brain, Mic, Database, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorChatConfig() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const isEditMode = true;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [groupedVoices, setGroupedVoices] = useState({ cloned: [], professional: [], premade: [] });
  const [availableKBChunks, setAvailableKBChunks] = useState([]);
  const [loadingKBChunks, setLoadingKBChunks] = useState(false);
  const [showKBChunkSelector, setShowKBChunkSelector] = useState(false);
  const [coachingNotes, setCoachingNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newCoachingNote, setNewCoachingNote] = useState({ category: '', content: '' });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteData, setEditingNoteData] = useState({ category: '', content: '' });

  const [formData, setFormData] = useState({
    default_llm_model: "",
    fallback_models: [],
    system_prompt: "",
    guard_rail_enabled: true,
    guard_rail_strictness: "moderate",
    guard_rail_model: "google/gemini-2.0-flash-exp:free",
    custom_refusal_message: "",
    voice_mode: "openai",
    openai_voice_preference: "alloy",
    voice_id: "",
    voice_name: "",
    voice_speed: 1.0,
    voice_stability: 0.5,
    voice_similarity: 0.75,
    voice_style: 0.0,
    voice_speaker_boost: true,
    kb_similarity_threshold: 0.7,
    voice_ai_kb_item_limit: 100,
    selected_kb_chunk_ids: [],
    voice_profile_fields: ["about", "work_profile", "skills", "experience_level", "industries_served", "pricing", "case_studies", "customer_reviews"],
    voice_include_feedback: true,
    voice_feedback_types: ["user", "admin"],
    enable_tag_functions: false,
    filter_used_kb_items: false,
    chat_history_limit: 20,
    vad_sensitivity_threshold: 0.5,
    vad_silence_duration_ms: 700,
    enable_true_barge_in: false,
  });

  useEffect(() => {
    fetchActorData();
    fetchOpenRouterModels();
    fetchVoices();
    fetchKBChunks();
    fetchCoachingNotes();
  }, [actorId]);

  const fetchActorData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const actor = response.data;
      setFormData({
        ...formData,
        ...actor,
        voice_ai_kb_item_limit: actor.voice_ai_kb_item_limit || 100,
        selected_kb_chunk_ids: actor.selected_kb_chunk_ids || [],
        voice_profile_fields: actor.voice_profile_fields || ["about", "work_profile", "skills", "experience_level", "industries_served", "pricing", "case_studies", "customer_reviews"],
        voice_include_feedback: actor.voice_include_feedback !== undefined ? actor.voice_include_feedback : true,
        voice_feedback_types: actor.voice_feedback_types || ["user", "admin"],
      });
    } catch (error) {
      toast.error("Failed to fetch actor data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenRouterModels = async () => {
    try {
      setLoadingModels(true);
      const response = await axios.get(`${API}/openrouter/models`);
      setOpenrouterModels(response.data.models || []);
    } catch (error) {
      console.error("Failed to fetch OpenRouter models:", error);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchVoices = async () => {
    setLoadingVoices(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/elevenlabs/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const voicesList = response.data.voices || [];
      setVoices(voicesList);
      
      const grouped = {
        cloned: voicesList.filter(v => v.category === 'cloned'),
        professional: voicesList.filter(v => v.category === 'professional'),
        premade: voicesList.filter(v => v.category === 'premade' || !v.category)
      };
      setGroupedVoices(grouped);
    } catch (error) {
      console.error("Failed to fetch voices:", error);
    } finally {
      setLoadingVoices(false);
    }
  };

  const fetchKBChunks = async () => {
    setLoadingKBChunks(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}/kb-chunks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableKBChunks(response.data.kb_chunks || []);
    } catch (error) {
      console.error("Failed to fetch KB chunks:", error);
      toast.error("Failed to load Knowledge Base chunks");
    } finally {
      setLoadingKBChunks(false);
    }
  };

  const fetchCoachingNotes = async () => {
    try {
      setLoadingNotes(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/ai-actors/${actorId}/coaching-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoachingNotes(response.data.coaching_notes || []);
    } catch (error) {
      console.error("Failed to fetch coaching notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddCoachingNote = async () => {
    if (!newCoachingNote.category || !newCoachingNote.content) {
      toast.error("Please fill in both category and content");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-actors/${actorId}/coaching-notes`,
        newCoachingNote,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coaching note added successfully");
      setNewCoachingNote({ category: '', content: '' });
      fetchCoachingNotes();
    } catch (error) {
      toast.error("Failed to add coaching note");
      console.error(error);
    }
  };

  const handleUpdateCoachingNote = async (noteId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/ai-actors/${actorId}/coaching-notes/${noteId}`,
        editingNoteData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coaching note updated successfully");
      setEditingNoteId(null);
      setEditingNoteData({ category: '', content: '' });
      fetchCoachingNotes();
    } catch (error) {
      toast.error("Failed to update coaching note");
      console.error(error);
    }
  };

  const handleDeleteCoachingNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this coaching note?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/ai-actors/${actorId}/coaching-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Coaching note deleted");
      fetchCoachingNotes();
    } catch (error) {
      toast.error("Failed to delete coaching note");
      console.error(error);
    }
  };

  const handleSave = async () => {
    if (!formData.default_llm_model) {
      toast.error("Please select a default LLM model");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      const currentResponse = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentActor = currentResponse.data;
      
      const sanitizeNumericField = (value) => {
        if (value === "" || value === null || value === undefined) {
          return undefined;
        }
        const num = Number(value);
        return isNaN(num) ? undefined : num;
      };
      
      const sanitizedNumericFields = {
        voice_speed: sanitizeNumericField(formData.voice_speed),
        voice_stability: sanitizeNumericField(formData.voice_stability),
        voice_similarity: sanitizeNumericField(formData.voice_similarity),
        voice_style: sanitizeNumericField(formData.voice_style),
        vad_sensitivity_threshold: sanitizeNumericField(formData.vad_sensitivity_threshold),
        vad_silence_duration_ms: sanitizeNumericField(formData.vad_silence_duration_ms),
        chat_history_limit: sanitizeNumericField(formData.chat_history_limit),
        voice_ai_kb_item_limit: sanitizeNumericField(formData.voice_ai_kb_item_limit),
        kb_similarity_threshold: sanitizeNumericField(formData.kb_similarity_threshold)
      };
      
      Object.keys(sanitizedNumericFields).forEach(key => {
        if (sanitizedNumericFields[key] === undefined) {
          delete sanitizedNumericFields[key];
        }
      });
      
      const mergedData = {
        ...currentActor,
        ...formData,
        ...sanitizedNumericFields
      };
      
      await axios.put(`${API}/ai/actors/${actorId}`, mergedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("AI Configuration updated successfully");
    } catch (error) {
      toast.error("Failed to update configuration");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/ai/actors/manage/${actorId}`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        <h1 className="text-2xl font-bold">AI Configuration (Chat)</h1>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <Tabs defaultValue="ai-config" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ai-config" className="flex items-center gap-2">
            <Brain className="w-4 h-4" />
            AI Configuration
          </TabsTrigger>
          <TabsTrigger value="voice" className="flex items-center gap-2">
            <Mic className="w-4 h-4" />
            Voice
          </TabsTrigger>
          <TabsTrigger value="knowledge-base" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Knowledge Base
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ai-config" className="space-y-6">
                <div>
                  <Label className="text-red-600">Default LLM Model *</Label>
                  <Select
                    value={formData.default_llm_model}
                    onValueChange={(value) => setFormData({...formData, default_llm_model: value})}
                    required
                  >
                    <SelectTrigger className="border-red-200">
                      <SelectValue placeholder={loadingModels ? "Loading models..." : "Select default AI model (required)"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px] overflow-y-auto">
                      {loadingModels ? (
                        <SelectItem value="loading" disabled>Loading models...</SelectItem>
                      ) : openrouterModels.length > 0 ? (
                        <>
                          {/* Group by provider - similar to API Keys settings */}
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Free Models</div>
                          {openrouterModels.filter(m => m.id.includes(':free')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">OpenAI</div>
                          {openrouterModels.filter(m => m.id.startsWith('openai/') && !m.id.includes(':free')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Anthropic</div>
                          {openrouterModels.filter(m => m.id.startsWith('anthropic/')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Google</div>
                          {openrouterModels.filter(m => m.id.startsWith('google/') && !m.id.includes(':free')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Other Models</div>
                          {openrouterModels.filter(m => 
                            !m.id.startsWith('openai/') && 
                            !m.id.startsWith('anthropic/') && 
                            !m.id.startsWith('google/') &&
                            !m.id.includes(':free')
                          ).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                        </>
                      ) : (
                        <SelectItem value="none" disabled>No models available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    This model will be used for all AI activities performed by this actor unless overridden.
                  </p>
                </div>

                {/* FALLBACK MODELS - MULTI-LEVEL PRIORITY CHAIN */}
                <Card className="border-2 border-amber-200 bg-amber-50/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-amber-600" />
                      Fallback Model Chain (Auto-Switching)
                    </CardTitle>
                    <CardDescription>
                      Configure multiple fallback models in priority order. If primary fails, system will try each model until one succeeds.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Display current fallback chain */}
                    <div className="space-y-2">
                      {formData.fallback_models && formData.fallback_models.length > 0 ? (
                        formData.fallback_models.map((model, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="bg-amber-100 text-amber-800">
                                {index + 1}
                              </Badge>
                              <span className="text-sm font-medium">{model}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {index > 0 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newModels = [...formData.fallback_models];
                                    [newModels[index], newModels[index - 1]] = [newModels[index - 1], newModels[index]];
                                    setFormData({...formData, fallback_models: newModels});
                                  }}
                                >
                                  ↑
                                </Button>
                              )}
                              {index < formData.fallback_models.length - 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newModels = [...formData.fallback_models];
                                    [newModels[index], newModels[index + 1]] = [newModels[index + 1], newModels[index]];
                                    setFormData({...formData, fallback_models: newModels});
                                  }}
                                >
                                  ↓
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newModels = formData.fallback_models.filter((_, i) => i !== index);
                                  setFormData({...formData, fallback_models: newModels});
                                }}
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No fallback models configured. Add models below.
                        </p>
                      )}
                    </div>

                    {/* Add new fallback model */}
                    {formData.fallback_models && formData.fallback_models.length < 5 && (
                      <div>
                        <Label>Add Fallback Model (up to 5)</Label>
                        <Select
                          value=""
                          onValueChange={(value) => {
                            if (value && !formData.fallback_models.includes(value) && value !== formData.default_llm_model) {
                              setFormData({
                                ...formData,
                                fallback_models: [...formData.fallback_models, value]
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select model to add" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[400px] overflow-y-auto">
                            {loadingModels ? (
                              <SelectItem value="loading" disabled>Loading models...</SelectItem>
                            ) : openrouterModels.length > 0 ? (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Recommended Free Models</div>
                                <SelectItem value="google/gemini-2.0-flash-exp:free">Google Gemini 2.0 Flash (Free)</SelectItem>
                                <SelectItem value="deepseek/deepseek-r1:free">DeepSeek R1 (Free)</SelectItem>
                                <SelectItem value="qwen/qwen-2.5-72b-instruct:free">Qwen 2.5 72B (Free)</SelectItem>
                                
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">All Free Models</div>
                                {openrouterModels.filter(m => m.id.includes(':free')).map((model) => (
                                  <SelectItem key={model.id} value={model.id}>
                                    {model.name || model.id}
                                  </SelectItem>
                                ))}
                                
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Paid Models</div>
                                {openrouterModels.filter(m => !m.id.includes(':free')).slice(0, 50).map((model) => (
                                  <SelectItem key={model.id} value={model.id}>
                                    {model.name || model.id}
                                  </SelectItem>
                                ))}
                              </>
                            ) : (
                              <SelectItem value="none" disabled>No models available</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Models will be tried in order: Primary → 1st Fallback → 2nd Fallback → etc.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* System Prompt */}
                <div>
                  <Label>System Prompt for Chat</Label>
                  <Textarea
                    value={formData.system_prompt}
                    onChange={(e) => setFormData({...formData, system_prompt: e.target.value})}
                    placeholder="You are a helpful AI assistant specializing in..."
                    rows={15}
                    className="resize-y min-h-[300px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This prompt will be combined with the actor's profile data to guide chat responses.
                  </p>
                </div>

                {/* Guard Rail Settings */}
                <Card className="border-2 border-indigo-200 bg-indigo-50/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-indigo-600" />
                      Guard Rail Settings (Expertise Boundary Protection)
                    </CardTitle>
                    <CardDescription>
                      Control how strictly the AI Actor stays within their expertise when chatting
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Guard Rail Enabled Toggle */}
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                      <div className="flex-1">
                        <Label htmlFor="guard_rail_enabled" className="font-medium">
                          Enable Guard Rail
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Pre-filter questions to ensure they align with this actor's expertise
                        </p>
                      </div>
                      <Switch
                        id="guard_rail_enabled"
                        checked={formData.guard_rail_enabled}
                        onCheckedChange={(checked) => setFormData({...formData, guard_rail_enabled: checked})}
                      />
                    </div>

                    {formData.guard_rail_enabled && (
                      <>
                        {/* Strictness Level */}
                        <div>
                          <Label>Strictness Level</Label>
                          <Select
                            value={formData.guard_rail_strictness}
                            onValueChange={(value) => setFormData({...formData, guard_rail_strictness: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select strictness level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="strict">
                                <div className="flex flex-col">
                                  <span className="font-medium">Strict</span>
                                  <span className="text-xs text-muted-foreground">Only exact expertise matches</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="moderate">
                                <div className="flex flex-col">
                                  <span className="font-medium">Moderate (Recommended)</span>
                                  <span className="text-xs text-muted-foreground">Allow somewhat related questions</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="loose">
                                <div className="flex flex-col">
                                  <span className="font-medium">Loose</span>
                                  <span className="text-xs text-muted-foreground">Block only obviously unrelated topics</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Guard Rail Model */}
                        <div>
                          <Label>Guard Rail Model</Label>
                          <Select
                            value={formData.guard_rail_model}
                            onValueChange={(value) => setFormData({...formData, guard_rail_model: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select model for guard rail checks" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="google/gemini-2.0-flash-exp:free">
                                Google Gemini 2.0 Flash (Free)
                              </SelectItem>
                              <SelectItem value="openai/gpt-4o-mini">
                                OpenAI GPT-4o Mini (Paid - More Reliable)
                              </SelectItem>
                              <SelectItem value="anthropic/claude-3-haiku">
                                Anthropic Claude 3 Haiku (Paid - Fast)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            Lightweight model used to check question relevance before main AI response
                          </p>
                        </div>

                        {/* Custom Refusal Message */}
                        <div>
                          <Label>Custom Refusal Message (Optional)</Label>
                          <Textarea
                            value={formData.custom_refusal_message}
                            onChange={(e) => setFormData({...formData, custom_refusal_message: e.target.value})}
                            placeholder="Leave blank for default message, or customize: 'I appreciate your question, but I focus exclusively on...'"
                            rows={4}
                            className="resize-y"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Message shown to users when their question is outside this actor's expertise
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Coaching Notes (Full CRUD Management) */}
                {isEditMode && (
                  <Card className="border-2 border-emerald-200 bg-emerald-50/30">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-emerald-600" />
                        Coaching Notes / Memory Bank (Admin Only)
                      </CardTitle>
                      <CardDescription>
                        Add, edit, and manage coaching notes to guide this AI Actor's responses
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Add New Note Form */}
                      <div className="border rounded-lg p-4 bg-white space-y-3">
                        <h4 className="font-semibold text-sm">Add New Coaching Note</h4>
                        <div>
                          <Label>Category</Label>
                          <Input
                            placeholder="e.g., pricing, technical, communication"
                            value={newCoachingNote.category}
                            onChange={(e) => setNewCoachingNote({...newCoachingNote, category: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Content</Label>
                          <Textarea
                            placeholder="Add guidance or important information for this AI Actor..."
                            rows={5}
                            className="resize-y min-h-[120px]"
                            value={newCoachingNote.content}
                            onChange={(e) => setNewCoachingNote({...newCoachingNote, content: e.target.value})}
                          />
                        </div>
                        <Button
                          onClick={handleAddCoachingNote}
                          disabled={!newCoachingNote.category || !newCoachingNote.content}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Coaching Note
                        </Button>
                      </div>

                      {/* Existing Notes */}
                      {loadingNotes ? (
                        <p className="text-sm text-muted-foreground">Loading coaching notes...</p>
                      ) : coachingNotes.length === 0 ? (
                        <div className="p-4 border rounded-lg bg-white text-center">
                          <p className="text-sm text-muted-foreground">
                            No coaching notes yet. Add notes above to improve this AI Actor's responses.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Group by category */}
                          {Object.entries(
                            coachingNotes.reduce((acc, note) => {
                              const cat = note.category || 'Other';
                              if (!acc[cat]) acc[cat] = [];
                              acc[cat].push(note);
                              return acc;
                            }, {})
                          ).map(([category, notes]) => (
                            <div key={category} className="border rounded-lg p-3 bg-white">
                              <h4 className="font-semibold text-sm text-emerald-900 mb-2 uppercase">
                                {category}
                              </h4>
                              <div className="space-y-2">
                                {notes.map((note) => (
                                  <div key={note.id} className="p-2 bg-emerald-50 rounded">
                                    {editingNoteId === note.id ? (
                                      /* Edit Mode */
                                      <div className="space-y-2">
                                        <Input
                                          value={editingNoteData.category}
                                          onChange={(e) => setEditingNoteData({...editingNoteData, category: e.target.value})}
                                          placeholder="Category"
                                        />
                                        <Textarea
                                          value={editingNoteData.content}
                                          onChange={(e) => setEditingNoteData({...editingNoteData, content: e.target.value})}
                                          rows={3}
                                        />
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            onClick={() => handleUpdateCoachingNote(note.id)}
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                          >
                                            Save
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              setEditingNoteId(null);
                                              setEditingNoteData({ category: '', content: '' });
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      /* View Mode */
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <p className="text-sm text-slate-700">{note.content}</p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            Added by {note.created_by} on {new Date(note.created_at).toLocaleDateString()}
                                          </p>
                                        </div>
                                        <div className="flex gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              setEditingNoteId(note.id);
                                              setEditingNoteData({
                                                category: note.category,
                                                content: note.content
                                              });
                                            }}
                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteCoachingNote(note.id)}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                          >
                                            Delete
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        💡 You can also add coaching notes from Chat Moderation by viewing conversations
                      </p>
                    </CardContent>
                  </Card>
                )}


        </TabsContent>

        <TabsContent value="voice" className="space-y-6">
                {/* Voice Configuration for Public Profile Calls */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-purple-600" />
                    <div>
                      <Label className="text-base font-semibold text-purple-900">Voice Call Configuration</Label>
                      <p className="text-xs text-slate-600 mt-1">Configure voice settings for public profile "Start a Call" feature</p>
                    </div>
                  </div>

                  {/* Voice Mode Selection */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-purple-900">Voice Mode</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="voice_mode"
                          value="openai"
                          checked={formData.voice_mode === "openai"}
                          onChange={(e) => setFormData({...formData, voice_mode: e.target.value})}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm font-medium">⚡ OpenAI Voice (Fast, Low Latency)</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="voice_mode"
                          value="elevenlabs"
                          checked={formData.voice_mode === "elevenlabs"}
                          onChange={(e) => setFormData({...formData, voice_mode: e.target.value})}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm font-medium">🎙️ ElevenLabs Voice (Custom Clone)</span>
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formData.voice_mode === "openai" 
                        ? "OpenAI voices provide near real-time responses"
                        : "ElevenLabs uses your custom voice clone (requires voice assignment in Voice Settings)"
                      }
                    </p>
                  </div>

                  {/* OpenAI Voice Selection (when OpenAI mode is selected) */}
                  {formData.voice_mode === "openai" && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">OpenAI Voice</Label>
                      <Select 
                        value={formData.openai_voice_preference} 
                        onValueChange={(value) => setFormData({...formData, openai_voice_preference: value})}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select OpenAI voice" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alloy">Alloy (Neutral)</SelectItem>
                          <SelectItem value="echo">Echo (Male)</SelectItem>
                          <SelectItem value="fable">Fable (British Male)</SelectItem>
                          <SelectItem value="onyx">Onyx (Deep Male)</SelectItem>
                          <SelectItem value="nova">Nova (Female)</SelectItem>
                          <SelectItem value="shimmer">Shimmer (Soft Female)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* ElevenLabs Voice Selection & Settings (when ElevenLabs mode is selected) */}
                  {formData.voice_mode === "elevenlabs" && (
                    <div className="space-y-4">
                      {/* Voice Selection */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">ElevenLabs Voice</Label>
                        <Select 
                          value={formData.voice_id} 
                          onValueChange={(value) => {
                            const selectedVoice = voices.find(v => v.voice_id === value);
                            setFormData({
                              ...formData, 
                              voice_id: value,
                              voice_name: selectedVoice?.name || ""
                            });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={loadingVoices ? "Loading voices..." : "Select a voice"} />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {loadingVoices ? (
                              <SelectItem value="loading" disabled>Loading voices...</SelectItem>
                            ) : (
                              <>
                                {groupedVoices.cloned.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100">
                                      Your Voices ({groupedVoices.cloned.length})
                                    </div>
                                    {groupedVoices.cloned.map((voice) => (
                                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                        <div className="flex items-center gap-2">
                                          <span>⭐</span>
                                          <span>{voice.name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {groupedVoices.professional.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                                      Professional Voices ({groupedVoices.professional.length})
                                    </div>
                                    {groupedVoices.professional.map((voice) => (
                                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                        <div className="flex items-center gap-2">
                                          <span>🎙️</span>
                                          <span>{voice.name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {groupedVoices.premade.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                                      Premade Voices ({groupedVoices.premade.length})
                                    </div>
                                    {groupedVoices.premade.map((voice) => (
                                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                        <div className="flex items-center gap-2">
                                          <span>🔊</span>
                                          <span>{voice.name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Voice Settings Sliders */}
                      {formData.voice_id && (
                        <div className="space-y-4 p-4 bg-white rounded-lg border border-purple-200">
                          <h4 className="text-sm font-semibold text-purple-900">Voice Settings</h4>
                          
                          {/* Speed */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Speed</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_speed.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0.5"
                              max="1.5"
                              step="0.1"
                              value={formData.voice_speed}
                              onChange={(e) => setFormData({...formData, voice_speed: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Stability */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Stability</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_stability.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={formData.voice_stability}
                              onChange={(e) => setFormData({...formData, voice_stability: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Similarity */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Similarity Boost</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_similarity.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={formData.voice_similarity}
                              onChange={(e) => setFormData({...formData, voice_similarity: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Style */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Style Exaggeration</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_style.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={formData.voice_style}
                              onChange={(e) => setFormData({...formData, voice_style: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Speaker Boost */}
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Speaker Boost</Label>
                            <input
                              type="checkbox"
                              checked={formData.voice_speaker_boost}
                              onChange={(e) => setFormData({...formData, voice_speaker_boost: e.target.checked})}
                              className="w-4 h-4"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Voice AI Profile & Feedback Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-blue-900">Voice AI Profile & Feedback Settings</Label>
                      <p className="text-xs text-slate-600 mt-1">Choose what information to include in voice conversations</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Profile Fields Selection */}
                    <div>
                      <Label className="text-sm font-medium text-blue-900 mb-2 block">Profile Fields to Include</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "about", label: "About" },
                          { value: "work_profile", label: "Work Profile" },
                          { value: "skills", label: "Skills" },
                          { value: "experience_level", label: "Experience Level" },
                          { value: "industries_served", label: "Industries" },
                          { value: "pricing", label: "Pricing" },
                          { value: "case_studies", label: "Case Studies" },
                          { value: "customer_reviews", label: "Customer Reviews" }
                        ].map((field) => (
                          <label key={field.value} className="flex items-center gap-2 p-2 bg-white rounded border border-blue-100 hover:bg-blue-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.voice_profile_fields || []).includes(field.value)}
                              onChange={(e) => {
                                const currentFields = formData.voice_profile_fields || [];
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    voice_profile_fields: [...currentFields, field.value]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    voice_profile_fields: currentFields.filter(f => f !== field.value)
                                  });
                                }
                              }}
                            />
                            <span className="text-xs font-medium text-slate-700">{field.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Feedback Configuration */}
                    <div className="pt-3 border-t border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium text-blue-900">Include Feedback</Label>
                        <Switch
                          checked={formData.voice_include_feedback}
                          onCheckedChange={(checked) => setFormData({...formData, voice_include_feedback: checked})}
                        />
                      </div>
                      
                      {formData.voice_include_feedback && (
                        <div className="mt-2 space-y-2">
                          <Label className="text-xs text-slate-600">Feedback Types:</Label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: "user", label: "User Feedback" },
                              { value: "admin", label: "Admin Reviews" },
                              { value: "positive", label: "Positive Only" },
                              { value: "negative", label: "Negative Only" }
                            ].map((type) => (
                              <label key={type.value} className="flex items-center gap-2 p-2 bg-white rounded border border-blue-100 hover:bg-blue-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(formData.voice_feedback_types || []).includes(type.value)}
                                  onChange={(e) => {
                                    const currentTypes = formData.voice_feedback_types || [];
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        voice_feedback_types: [...currentTypes, type.value]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        voice_feedback_types: currentTypes.filter(t => t !== type.value)
                                      });
                                    }
                                  }}
                                />
                                <span className="text-xs font-medium text-slate-700">{type.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tag-Based Function Calling Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <div className="flex-1">
                      <Label className="text-base font-semibold text-amber-900">Tag-Based Function Calling (Fallback)</Label>
                      <p className="text-xs text-slate-600 mt-1">Enable automatic KB search by tags when static items don't have the answer</p>
                    </div>
                    <Switch
                      checked={formData.enable_tag_functions}
                      onCheckedChange={(checked) => setFormData({...formData, enable_tag_functions: checked})}
                    />
                  </div>

                  {formData.enable_tag_functions && (
                    <div className="space-y-3 pl-8">
                      <div className="bg-white p-4 rounded border border-amber-200">
                        <h4 className="text-sm font-semibold text-amber-900 mb-2">🎯 How It Works:</h4>
                        <ol className="space-y-2 text-xs text-amber-800">
                          <li className="flex gap-2">
                            <span className="font-bold">1.</span>
                            <span>Voice AI tries to answer from loaded KB items (static selection)</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">2.</span>
                            <span>If no answer found, AI automatically calls the search function</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">3.</span>
                            <span>System extracts keywords from user's question</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">4.</span>
                            <span>Searches KB items that have matching tags</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">5.</span>
                            <span>Returns relevant items to AI for response</span>
                          </li>
                        </ol>
                        
                        <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-300">
                          <p className="text-xs text-amber-900">
                            <strong>📋 Example:</strong><br/>
                            User asks: "What's your pricing?"<br/>
                            → AI extracts keywords: <code className="bg-amber-100 px-1 rounded">pricing, costs</code><br/>
                            → Searches KB items with tags: pricing, costs, packages, payment<br/>
                            → Returns matching items → AI responds with pricing info
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded border border-amber-200">
                        <p className="text-xs text-amber-800">
                          <strong>⚠️ Important:</strong> Your KB items must have relevant tags assigned for this to work.
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Go to <strong>Knowledge Base</strong> page → Edit items → Add tags like: pricing, services, company, technical, contact, etc.
                        </p>
                      </div>
                      
                      <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                        <p className="text-xs text-emerald-800">
                          <strong>✅ Benefits:</strong> Works with unlimited tags • Searches any keywords • No predefined categories • AI decides when to search
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Voice AI Conversation Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-purple-900">Voice AI Conversation Configuration</Label>
                      <p className="text-xs text-slate-600 mt-1">Control KB filtering and chat history for better conversations</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* KB Item Filtering Toggle */}
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-purple-100">
                      <div className="flex-1">
                        <Label className="text-sm font-medium text-purple-900">Filter Used KB Items</Label>
                        <p className="text-xs text-slate-600 mt-1">Prevent AI from repeating the same information by filtering out already-used KB items</p>
                      </div>
                      <Switch
                        checked={formData.filter_used_kb_items ?? false}
                        onCheckedChange={(checked) => setFormData({...formData, filter_used_kb_items: checked})}
                      />
                    </div>

                    {/* Chat History Limit */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-purple-900">Chat History Limit</Label>
                        <span className="text-sm font-semibold text-purple-600">{formData.chat_history_limit ?? 20} messages</span>
                      </div>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        value={formData.chat_history_limit ?? 20}
                        onChange={(e) => setFormData({...formData, chat_history_limit: parseInt(e.target.value)})}
                        className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>5 (Minimal context)</span>
                        <span>25 (Balanced)</span>
                        <span>50 (Full history)</span>
                      </div>
                      <p className="text-xs text-slate-600">Number of previous messages to include in conversation context</p>
                    </div>
                  </div>
                </div>

                {/* Voice AI VAD Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-cyan-900">Voice Activity Detection (VAD) Settings</Label>
                      <p className="text-xs text-slate-600 mt-1">Adjust voice detection sensitivity and interruption behavior</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* VAD Sensitivity */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-cyan-900">Sensitivity Threshold</Label>
                        <span className="text-sm font-semibold text-cyan-600">{(formData.vad_sensitivity_threshold ?? 0.5).toFixed(2)}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={formData.vad_sensitivity_threshold ?? 0.5}
                        onChange={(e) => setFormData({...formData, vad_sensitivity_threshold: parseFloat(e.target.value)})}
                        className="w-full h-2 bg-cyan-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>0.0 (Very sensitive)</span>
                        <span>0.5 (Balanced)</span>
                        <span>1.0 (Less sensitive)</span>
                      </div>
                      <p className="text-xs text-slate-600">How sensitive the system is to detecting speech. Lower = picks up quieter sounds</p>
                    </div>

                    {/* Silence Duration */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-cyan-900">Silence Duration</Label>
                        <span className="text-sm font-semibold text-cyan-600">{formData.vad_silence_duration_ms ?? 700}ms</span>
                      </div>
                      <input
                        type="range"
                        min="200"
                        max="2000"
                        step="100"
                        value={formData.vad_silence_duration_ms ?? 700}
                        onChange={(e) => setFormData({...formData, vad_silence_duration_ms: parseInt(e.target.value)})}
                        className="w-full h-2 bg-cyan-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>200ms (Quick)</span>
                        <span>700ms (Normal)</span>
                        <span>2000ms (Patient)</span>
                      </div>
                      <p className="text-xs text-slate-600">How long to wait for silence before considering speech finished</p>
                    </div>

                    {/* True Barge-In Toggle */}
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-cyan-100">
                      <div className="flex-1">
                        <Label className="text-sm font-medium text-cyan-900">Enable True Barge-In (Interruption)</Label>
                        <p className="text-xs text-slate-600 mt-1">Allow users to interrupt AI while it's speaking</p>
                        <p className="text-xs text-amber-600 mt-1">⚠️ Experimental: May cause audio glitches</p>
                      </div>
                      <Switch
                        checked={formData.enable_true_barge_in ?? false}
                        onCheckedChange={(checked) => setFormData({...formData, enable_true_barge_in: checked})}
                      />
                    </div>
                  </div>
                </div>
        </TabsContent>

        <TabsContent value="knowledge-base" className="space-y-6">
                {/* Knowledge Base Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-emerald-900">Knowledge Base Configuration</Label>
                      <p className="text-xs text-slate-600 mt-1">Configure how the AI searches and uses Knowledge Base content</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-emerald-900">
                        Semantic Search Similarity Threshold
                      </Label>
                      <span className="text-sm font-mono text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                        {formData.kb_similarity_threshold === 0 ? "No Threshold" : `${(formData.kb_similarity_threshold * 100).toFixed(0)}%`}
                      </span>
                    </div>
                    
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={formData.kb_similarity_threshold}
                      onChange={(e) => setFormData({...formData, kb_similarity_threshold: parseFloat(e.target.value)})}
                      className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                    
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>0 (No Filter)</span>
                      <span>0.5 (Moderate)</span>
                      <span>1.0 (Very Strict)</span>
                    </div>
                    
                    <p className="text-xs text-muted-foreground bg-white p-3 rounded border border-emerald-200">
                      {formData.kb_similarity_threshold === 0 ? (
                        <span>⚠️ <strong>No threshold:</strong> Returns top results regardless of relevance. Use for general queries.</span>
                      ) : formData.kb_similarity_threshold < 0.5 ? (
                        <span>📖 <strong>Low threshold ({(formData.kb_similarity_threshold * 100).toFixed(0)}%):</strong> Returns loosely related content. Good for exploratory questions.</span>
                      ) : formData.kb_similarity_threshold < 0.75 ? (
                        <span>🎯 <strong>Moderate threshold ({(formData.kb_similarity_threshold * 100).toFixed(0)}%):</strong> Returns moderately relevant content. Balanced approach.</span>
                      ) : (
                        <span>🔍 <strong>High threshold ({(formData.kb_similarity_threshold * 100).toFixed(0)}%):</strong> Returns only highly relevant content. Best for specific queries.</span>
                      )}
                    </p>
                    
                    {/* KB Item Limit */}
                    <div className="pt-4 border-t border-emerald-200">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-emerald-900">
                          Voice AI KB Item Limit
                        </Label>
                        <span className="text-sm font-mono text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                          {formData.voice_ai_kb_item_limit} items
                        </span>
                      </div>
                      
                      <input
                        type="range"
                        min="10"
                        max="200"
                        step="10"
                        value={formData.voice_ai_kb_item_limit}
                        onChange={(e) => setFormData({...formData, voice_ai_kb_item_limit: parseInt(e.target.value)})}
                        className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600 mt-2"
                      />
                      
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>10 (Fast)</span>
                        <span>100 (Balanced)</span>
                        <span>200 (Comprehensive)</span>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Maximum KB items to load at Voice AI session start. Higher = more context, slower loading.
                      </p>
                    </div>
                    
                    {/* KB Chunk Selection */}
                    <div className="pt-4 border-t border-emerald-200">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowKBChunkSelector(!showKBChunkSelector)}
                        className="w-full justify-between"
                        disabled={loadingKBChunks}
                      >
                        <span>
                          {showKBChunkSelector ? "Hide" : "Show"} KB Chunk Selection
                          {(formData.selected_kb_chunk_ids || []).length > 0 && ` (${(formData.selected_kb_chunk_ids || []).length} selected)`}
                        </span>
                        {loadingKBChunks && <span className="text-xs">Loading...</span>}
                      </Button>
                      
                      {showKBChunkSelector && (
                        <div className="mt-3 max-h-64 overflow-y-auto border border-emerald-200 rounded p-3 bg-white">
                          {(availableKBChunks || []).length === 0 ? (
                            <p className="text-xs text-slate-500 text-center py-4">
                              No KB chunks available. Associate Knowledge Bases first.
                            </p>
                          ) : (
                            <>
                              <div className="flex justify-between items-center mb-3 pb-2 border-b">
                                <p className="text-xs font-semibold text-emerald-900">
                                  Select specific chunks ({(formData.selected_kb_chunk_ids || []).length} of {(availableKBChunks || []).length})
                                </p>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if ((formData.selected_kb_chunk_ids || []).length === (availableKBChunks || []).length) {
                                      setFormData({...formData, selected_kb_chunk_ids: []});
                                    } else {
                                      setFormData({...formData, selected_kb_chunk_ids: (availableKBChunks || []).map(c => c.id)});
                                    }
                                  }}
                                  className="text-xs h-6"
                                >
                                  {(formData.selected_kb_chunk_ids || []).length === (availableKBChunks || []).length ? "Deselect All" : "Select All"}
                                </Button>
                              </div>
                              
                              <div className="space-y-2">
                                {(availableKBChunks || []).map((chunk) => (
                                  <label key={chunk.id} className="flex items-start gap-2 p-2 hover:bg-emerald-50 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={(formData.selected_kb_chunk_ids || []).includes(chunk.id)}
                                      onChange={(e) => {
                                        const currentIds = formData.selected_kb_chunk_ids || [];
                                        if (e.target.checked) {
                                          setFormData({
                                            ...formData,
                                            selected_kb_chunk_ids: [...currentIds, chunk.id]
                                          });
                                        } else {
                                          setFormData({
                                            ...formData,
                                            selected_kb_chunk_ids: currentIds.filter(id => id !== chunk.id)
                                          });
                                        }
                                      }}
                                      className="mt-1"
                                    />
                                    <div className="flex-1">
                                      <p className="text-xs font-semibold text-slate-700">{chunk.source_name}</p>
                                      <p className="text-xs text-slate-500 line-clamp-2">{chunk.content_preview}</p>
                                      <Badge variant="outline" className="text-xs mt-1">{chunk.knowledge_base_name}</Badge>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Leave empty to auto-select chunks. Select specific chunks to manually control what's loaded.
                      </p>
                    </div>
                  </div>
                </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
